import React, { useState, useEffect } from "react";
import {
  Box,
  Paper,
  Table,
  TableHead,
  TableBody,
  TableCell,
  TableRow,
  TableContainer,
  IconButton,
  Tooltip,
  Typography,
  Stack,
  Avatar,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Pagination,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Divider,
  useTheme,
} from "@mui/material";
import { Delete, Visibility } from "@mui/icons-material";
import Header from "../components/Header";
import axios from "axios";
import ConfirmDialog from "../components/common/ComfirmationDialog";

const PAGE_SIZE_OPTIONS = [5, 10, 25, 50];

function IssueTableRow({ issue, onView, onDelete }) {
  const reporter = issue.user || {};
  return (
    <TableRow hover>
      <TableCell className="notranslate">
        <Typography variant="body1" noWrap maxWidth={240}>
          {issue.message}
        </Typography>
      </TableCell>
      <TableCell className="notranslate">
        <Stack direction="row" spacing={1} alignItems="center">
          <Avatar src={reporter.avatar_url} sx={{ width: 34, height: 34 }}>
            {reporter.FullName?.[0] || "?"}
          </Avatar>
          <Typography fontWeight={600} noWrap>
            {reporter.FullName || "N/A"}
          </Typography>
        </Stack>
      </TableCell>
      <TableCell className="notranslate">
        <Typography variant="body2" noWrap>
          {reporter.email || "-"}
        </Typography>
      </TableCell>
      <TableCell className="notranslate" dir="ltr">
        <Typography variant="body2" noWrap>
          {reporter.phone || "-"}
        </Typography>
      </TableCell>
      <TableCell>
        <Typography variant="body2" sx={{ textTransform: "capitalize" }}>
          {reporter.role || "-"}
        </Typography>
      </TableCell>
      <TableCell>
        <Typography variant="body2" sx={{ textTransform: "capitalize" }}>
          {new Date(issue.createdAt).toLocaleString()}
        </Typography>
      </TableCell>
      <TableCell align="right">
        <Tooltip title="View">
          <IconButton color="primary" onClick={() => onView(issue)}>
            <Visibility />
          </IconButton>
        </Tooltip>
        <Tooltip title="Delete">
          <IconButton color="error" onClick={() => onDelete(issue)}>
            <Delete />
          </IconButton>
        </Tooltip>
      </TableCell>
    </TableRow>
  );
}

function IssueViewDialog({ open, onClose, issue }) {
  const theme = useTheme();
  if (!issue) return null;
  const reporter = issue.user || {};

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle sx={{ pb: 1, fontWeight: 700 }}>Issue Details</DialogTitle>
      <DialogContent dividers sx={{ p: 3 }}>
        <Stack spacing={2} alignItems="center">
          <Avatar
            src={reporter.avatar_url}
            sx={{
              width: 76,
              height: 76,
              boxShadow: 3,
              border: `3px solid ${theme.palette.primary.main}`,
              mb: 1,
            }}
            className="notranslate"
          >
            {reporter.FullName?.[0] || "?"}
          </Avatar>
          <Typography
            fontWeight={700}
            fontSize={22}
            gutterBottom
            className="notranslate"
          >
            {reporter.FullName || "N/A"}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            <b>Role:</b> {reporter.role || "-"}
          </Typography>
          <Divider sx={{ width: "100%" }} />
          <Stack spacing={1} width="100%">
            <Typography variant="subtitle2" color="primary">
              Message
            </Typography>
            <Typography
              sx={{ wordBreak: "break-word" }}
              className="notranslate"
            >
              {issue.message}
            </Typography>
          </Stack>
          <Divider sx={{ width: "100%" }} />
          <Stack spacing={0.5} width="100%">
            <Typography variant="subtitle2" color="primary">
              Contact
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {reporter.email || "-"}
            </Typography>
            <Typography variant="body2" color="text.secondary" dir="ltr">
              {reporter.phone || "-"}
            </Typography>
          </Stack>
          <Divider sx={{ width: "100%" }} />
          <Typography variant="caption" color="text.secondary">
            Created: {new Date(issue.createdAt).toLocaleString()}
          </Typography>
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button variant="contained" fullWidth onClick={onClose}>
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
}

const IssueManagementPage = () => {
  const [issues, setIssues] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(PAGE_SIZE_OPTIONS[1]); // default 10

  // Dialog State
  const [viewDialogOpen, setViewDialogOpen] = useState(false);
  const [selectedIssue, setSelectedIssue] = useState(null);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  // Fetch from API
  const fetchIssues = async () => {
    setLoading(true);
    try {
      const res = await axios.get(
        `${process.env.REACT_APP_BACKEND_URL}/api/admin/issues/getAll`
      );
      setIssues(res.data);
    } catch (err) {
      alert("Failed to load issues: " + err.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchIssues();
  }, []);

  // Pagination calculation
  const totalPages = Math.ceil(issues.length / pageSize);
  const paginatedIssues = issues.slice((page - 1) * pageSize, page * pageSize);

  // Handlers
  const handleView = (issue) => {
    setSelectedIssue(issue);
    setViewDialogOpen(true);
  };
  const handleDeleteRequest = (issue) => {
    setDeleteTarget(issue);
    setConfirmOpen(true);
  };

  const handleDeleteConfirmed = async () => {
    if (!deleteTarget) return;
    setDeleteLoading(true);
    try {
      await axios.delete(
        `${process.env.REACT_APP_BACKEND_URL}/api/admin/issues/delete/${deleteTarget.id}`
      );
      setIssues((prev) => prev.filter((i) => i.id !== deleteTarget.id));
      setConfirmOpen(false);
      setDeleteTarget(null);
    } catch (err) {
      alert("Delete failed: " + err.message);
    } finally {
      setDeleteLoading(false);
    }
  };

  // Reset page if page size changes
  useEffect(() => {
    setPage(1);
  }, [pageSize]);

  return (
    <Box className="main-container mt-4">
      <Header title="Issue Management" />
      <Paper sx={{ mt: 4, p: 3, borderRadius: 3, boxShadow: 2 }}>
        <Box
          mb={2}
          display="flex"
          alignItems="center"
          justifyContent="space-between"
        >
          <Typography variant="h6" fontWeight={700}>
            Issues
          </Typography>
          <FormControl size="small" sx={{ minWidth: 120 }}>
            <InputLabel>Rows per page</InputLabel>
            <Select
              value={pageSize}
              label="Rows per page"
              onChange={(e) => setPageSize(Number(e.target.value))}
            >
              {PAGE_SIZE_OPTIONS.map((size) => (
                <MenuItem key={size} value={size}>
                  {size}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>
        <TableContainer
          sx={{
            maxHeight: "65vh",
            overflow: "auto",
            minWidth: "100%",
            scrollbarWidth: "thin",
            "&::-webkit-scrollbar": {
              height: "8px",
              width: "8px",
            },
            "&::-webkit-scrollbar-track": {
              backgroundColor: "#f0f0f0",
              borderRadius: "8px",
            },
            "&::-webkit-scrollbar-thumb": {
              backgroundColor: "#c1c1c1",
              borderRadius: "8px",
              border: "2px solid #f0f0f0",
            },
            "&::-webkit-scrollbar-thumb:hover": {
              backgroundColor: "#a0a0a0",
            },
          }}
        >
          <Table stickyHeader>
            <TableHead>
              <TableRow>
                <TableCell>
                  <b>Message</b>
                </TableCell>
                <TableCell>
                  <b>Reporter</b>
                </TableCell>
                <TableCell>
                  <b>Email</b>
                </TableCell>
                <TableCell>
                  <b>Phone</b>
                </TableCell>
                <TableCell>
                  <b>Role</b>
                </TableCell>
                <TableCell>
                  <b>Report Time</b>
                </TableCell>
                <TableCell align="right">
                  <b>Actions</b>
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={6} align="center">
                    <CircularProgress size={32} />
                  </TableCell>
                </TableRow>
              ) : paginatedIssues.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={6} align="center">
                    <Typography color="text.secondary">
                      No issues found.
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                paginatedIssues.map((issue) => (
                  <IssueTableRow
                    key={issue.id}
                    issue={issue}
                    onView={handleView}
                    onDelete={handleDeleteRequest}
                  />
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
        <Box mt={3} display="flex" justifyContent="center">
          <Pagination
            count={totalPages}
            page={page}
            onChange={(_, value) => setPage(value)}
            color="primary"
            shape="rounded"
            showFirstButton
            showLastButton
            siblingCount={1}
            boundaryCount={1}
          />
        </Box>
      </Paper>
      <IssueViewDialog
        open={viewDialogOpen}
        onClose={() => setViewDialogOpen(false)}
        issue={selectedIssue}
      />
      <ConfirmDialog
        open={confirmOpen}
        onClose={() => setConfirmOpen(false)}
        onConfirm={handleDeleteConfirmed}
        title="Delete Issue"
        content={
          deleteTarget
            ? `Are you sure you want to delete this issue reported by "${
                deleteTarget.user?.FullName || "unknown"
              }"?`
            : ""
        }
        loading={deleteLoading}
      />
    </Box>
  );
};

export default IssueManagementPage;
